import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BatchCompanyCandidate,
  BulkProcessingRun,
  CompanyBulkProcessingRequest,
  ExtractorName,
  getBulkProcessingRun,
  listBatchCompanies,
  startCompanyBulkProcessing,
} from '../../api/documentIntelligence';
import { getSozoChannels, getSozoServers } from '../../api/contentCompletion';
import { CORPORATE_STATUS_OPTIONS } from '../../constants/corporateStatus';
import { COVERAGE_PRIORITY_OPTIONS } from '../../constants/coveragePriority';
import { AuthContext } from '../../context/AuthContext';
import { SozoChannelInfo, SozoServerInfo } from '../../types/contentCompletion';

interface BatchProcessingPanelProps {
  daysBack: number;
  environment: string;
}

const makeIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
    const random = Math.floor(Math.random() * 16);
    return (value === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

const terminalRun = (run: BulkProcessingRun): boolean =>
  ['completed', 'partial', 'failed'].includes(run.status);

const analysisLabel = (name: ExtractorName): string =>
  name === 'pipeline_intelligence'
    ? 'Pipeline'
    : name === 'corporate_transactions'
    ? 'Corporate Transactions'
    : 'Milestones';

const analysisErrorMessages = (error?: string | null): string[] => {
  if (!error) return [];
  return Array.from(
    new Set(
      error
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
          const delimiter = part.indexOf(': ');
          return part.startsWith('preview:') && delimiter >= 0
            ? part.slice(delimiter + 2)
            : part;
        })
    )
  );
};

const activeRunStorageKey = (environment: string, actorId: string): string =>
  `babbage:company-bulk-run:${environment}:${actorId}`;

const pendingRunStorageKey = (environment: string, actorId: string): string =>
  `babbage:company-bulk-pending:${environment}:${actorId}`;

interface PendingCompanyBulkRun {
  idempotencyKey: string;
  request: CompanyBulkProcessingRequest;
}

const responseStatus = (caught: unknown): number | undefined =>
  (caught as { response?: { status?: number } })?.response?.status;

const retryableFailure = (caught: unknown): boolean => {
  const status = responseStatus(caught);
  return status === undefined || status === 408 || status === 429 || status >= 500;
};

export const BatchProcessingPanel: React.FC<BatchProcessingPanelProps> = ({
  daysBack,
  environment,
}) => {
  const auth = useContext(AuthContext);
  const actorId = auth?.user?.public_id || auth?.user?.email || 'anonymous';
  const requestGeneration = useRef(0);
  const pollGeneration = useRef(0);
  const startKey = useRef<string | null>(null);
  const [companies, setCompanies] = useState<BatchCompanyCandidate[]>([]);
  const [matchingDocumentCount, setMatchingDocumentCount] = useState(0);
  const [search, setSearch] = useState('');
  const [corporateStatus, setCorporateStatus] = useState('');
  const [coveragePriority, setCoveragePriority] = useState('');
  const [servers, setServers] = useState<SozoServerInfo[]>([]);
  const [channels, setChannels] = useState<SozoChannelInfo[]>([]);
  const [serverId, setServerId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [groupFilterError, setGroupFilterError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [selectedCompanies, setSelectedCompanies] = useState<Map<string, BatchCompanyCandidate>>(
    () => new Map()
  );
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [overrideProcessed, setOverrideProcessed] = useState(false);
  const [analyses, setAnalyses] = useState({
    milestones: true,
    pipeline_intelligence: true,
    corporate_transactions: false,
  });
  const [confirmCorporateWrites, setConfirmCorporateWrites] = useState(false);
  const [run, setRun] = useState<BulkProcessingRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [recoveryNonce, setRecoveryNonce] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = async (offset = 0, append = false) => {
    const generation = ++requestGeneration.current;
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const response = await listBatchCompanies({
        search: search.trim(),
        corporateStatus,
        coveragePriority,
        serverId: serverId || undefined,
        channelId: channelId || undefined,
        daysBack,
        offset,
        limit: 100,
      });
      if (generation !== requestGeneration.current) return;
      setCompanies((current) => (append ? [...current, ...response.companies] : response.companies));
      setSelectedCompanies((current) => {
        const next = new Map(current);
        response.companies.forEach((company) => {
          if (next.has(company.company_id)) next.set(company.company_id, company);
        });
        return next;
      });
      setMatchingDocumentCount(response.matching_document_count);
      setHasMore(response.has_more);
      setError(null);
    } catch {
      if (generation === requestGeneration.current) {
        setError('Could not load companies for batch processing.');
      }
    } finally {
      if (generation === requestGeneration.current) {
        append ? setLoadingMore(false) : setLoading(false);
      }
    }
  };

  useEffect(() => {
    void getSozoServers()
      .then((response) => {
        setServers(response.servers);
        setGroupFilterError(null);
      })
      .catch(() => setGroupFilterError('Server and focus-area filters are unavailable.'));
  }, []);

  useEffect(() => {
    setChannelId('');
    if (!serverId) {
      setChannels([]);
      setChannelsLoading(false);
      return;
    }
    let active = true;
    setChannelsLoading(true);
    void getSozoChannels(serverId)
      .then((response) => {
        if (!active) return;
        setChannels(response.channels);
        setGroupFilterError(null);
      })
      .catch(() => {
        if (!active) return;
        setChannels([]);
        setGroupFilterError('Focus-area filters are unavailable for this server.');
      })
      .finally(() => {
        if (active) setChannelsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [serverId]);

  useEffect(() => {
    requestGeneration.current += 1;
    setCompanies([]);
    setMatchingDocumentCount(0);
    setHasMore(false);
    setLoadingMore(false);
    setLoading(true);
    const handle = window.setTimeout(() => {
      void load(0, false);
    }, 200);
    return () => window.clearTimeout(handle);
    // load intentionally follows the current filter values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, corporateStatus, coveragePriority, serverId, channelId, daysBack, environment]);

  useEffect(() => {
    setSelected(new Set());
    setSelectedCompanies(new Map());
    setSelectAllMatching(false);
  }, [daysBack, environment]);

  useEffect(
    () => () => {
      requestGeneration.current += 1;
      pollGeneration.current += 1;
    },
    []
  );

  useEffect(() => {
    startKey.current = null;
    setError(null);
  }, [
    analyses,
    confirmCorporateWrites,
    corporateStatus,
    coveragePriority,
    serverId,
    channelId,
    daysBack,
    overrideProcessed,
    search,
    selectAllMatching,
    selected,
  ]);

  const selectedAnalyses = useMemo(
    () =>
      (Object.entries(analyses) as Array<[ExtractorName, boolean]>)
        .filter(([, enabled]) => enabled)
        .map(([name]) => name),
    [analyses]
  );

  const selectedDocumentCount = useMemo(
    () =>
      selectAllMatching
        ? matchingDocumentCount
        : Array.from(selectedCompanies.values()).reduce(
            (total, company) => total + company.document_count,
            0
          ),
    [matchingDocumentCount, selectAllMatching, selectedCompanies]
  );

  const selectedCompanyNames = useMemo(
    () => Array.from(selectedCompanies.values()).map((company) => company.company_name),
    [selectedCompanies]
  );

  const companyActivity = useMemo(
    () =>
      [...(run?.company_results || [])].sort(
        (left, right) => Date.parse(right.completed_at) - Date.parse(left.completed_at)
      ),
    [run]
  );

  const poll = async (runId: string, generation: number, restoringRun = false) => {
    let delayMs = 1000;
    for (;;) {
      const waitMs = delayMs;
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      if (generation !== pollGeneration.current) return;
      try {
        const next = await getBulkProcessingRun(runId);
        if (generation !== pollGeneration.current) return;
        setRun(next);
        setError(null);
        if (restoringRun) {
          setRestoring(false);
          restoringRun = false;
        }
        if (terminalRun(next)) {
          window.localStorage.removeItem(activeRunStorageKey(environment, actorId));
          return;
        }
        delayMs = 1000;
      } catch (caught) {
        if (generation === pollGeneration.current) {
          if (!retryableFailure(caught)) {
            window.localStorage.removeItem(activeRunStorageKey(environment, actorId));
            window.localStorage.removeItem(pendingRunStorageKey(environment, actorId));
            if (restoringRun) setRestoring(false);
            setError(`Batch status is unavailable (HTTP ${responseStatus(caught)}).`);
            return;
          }
          setError('Batch status could not be refreshed; retrying.');
        }
        delayMs = Math.min(delayMs * 2, 10000);
      }
    }
  };

  useEffect(() => {
    const generation = ++pollGeneration.current;
    const activeKey = activeRunStorageKey(environment, actorId);
    const pendingKey = pendingRunStorageKey(environment, actorId);
    const runId = window.localStorage.getItem(activeKey);
    const serializedPending = window.localStorage.getItem(pendingKey);
    setRun(null);
    setStarting(false);
    setRestoring(Boolean(runId || serializedPending));

    if (runId) {
      void getBulkProcessingRun(runId)
        .then((restored) => {
          if (generation !== pollGeneration.current) return;
          setRun(restored);
          setRestoring(false);
          setError(null);
          window.localStorage.removeItem(pendingKey);
          if (terminalRun(restored)) {
            window.localStorage.removeItem(activeKey);
          } else {
            void poll(runId, generation);
          }
        })
        .catch((caught) => {
          if (generation !== pollGeneration.current) return;
          if (!retryableFailure(caught)) {
            window.localStorage.removeItem(activeKey);
            window.localStorage.removeItem(pendingKey);
            setRestoring(false);
            setError(`Previous batch could not be restored (HTTP ${responseStatus(caught)}).`);
            return;
          }
          setError('Previous batch status could not be restored yet; retrying.');
          void poll(runId, generation, true);
        });
      return;
    }

    if (!serializedPending) return;
    let pending: PendingCompanyBulkRun;
    try {
      pending = JSON.parse(serializedPending) as PendingCompanyBulkRun;
      if (!pending.idempotencyKey || !pending.request) throw new Error('Invalid pending run');
    } catch {
      window.localStorage.removeItem(pendingKey);
      setRestoring(false);
      setError('The saved pending batch request was invalid and was cleared.');
      return;
    }

    const recoverPending = async () => {
      let delayMs = 1000;
      for (;;) {
        try {
          const restored = await startCompanyBulkProcessing(
            pending.request,
            pending.idempotencyKey
          );
          if (generation !== pollGeneration.current) return;
          startKey.current = null;
          window.localStorage.removeItem(pendingKey);
          setRun(restored);
          setRestoring(false);
          setError(null);
          if (terminalRun(restored)) {
            window.localStorage.removeItem(activeKey);
          } else {
            window.localStorage.setItem(activeKey, restored.run_id);
            void poll(restored.run_id, generation);
          }
          return;
        } catch (caught) {
          if (generation !== pollGeneration.current) return;
          if (!retryableFailure(caught)) {
            window.localStorage.removeItem(pendingKey);
            startKey.current = null;
            setRestoring(false);
            setError(`Pending batch could not be recovered (HTTP ${responseStatus(caught)}).`);
            return;
          }
          setError('Pending batch submission could not be recovered yet; retrying.');
          const waitMs = delayMs;
          await new Promise((resolve) => window.setTimeout(resolve, waitMs));
          if (generation !== pollGeneration.current) return;
          delayMs = Math.min(delayMs * 2, 10000);
        }
      }
    };
    void recoverPending();
    // Polling and recovery are intentionally bound to this environment generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, environment, recoveryNonce]);

  const start = async () => {
    if (restoring || (run && !terminalRun(run))) return;
    if (!selectAllMatching && selected.size === 0) {
      setError('Select at least one company.');
      return;
    }
    if (selectedAnalyses.length === 0) {
      setError('Select at least one analysis.');
      return;
    }
    if (analyses.corporate_transactions && !confirmCorporateWrites) {
      setError('Confirm canonical Corporate Transaction writes before starting.');
      return;
    }
    setStarting(true);
    setError(null);
    const generation = ++pollGeneration.current;
    const idempotencyKey = startKey.current ?? makeIdempotencyKey();
    startKey.current = idempotencyKey;
    const request: CompanyBulkProcessingRequest = {
      company_ids: selectAllMatching ? [] : Array.from(selected),
      select_all_matching: selectAllMatching,
      search: search.trim(),
      corporate_status: corporateStatus,
      coverage_priority: coveragePriority,
      server_id: serverId || null,
      channel_id: channelId || null,
      days_back: daysBack,
      analyses: selectedAnalyses,
      override_processed: overrideProcessed,
      confirm_corporate_transaction_canonical_write: confirmCorporateWrites,
    };
    window.localStorage.setItem(
      pendingRunStorageKey(environment, actorId),
      JSON.stringify({ idempotencyKey, request } satisfies PendingCompanyBulkRun)
    );
    try {
      const created = await startCompanyBulkProcessing(request, idempotencyKey);
      if (generation !== pollGeneration.current) return;
      startKey.current = null;
      window.localStorage.removeItem(pendingRunStorageKey(environment, actorId));
      setRun(created);
      if (terminalRun(created)) {
        window.localStorage.removeItem(activeRunStorageKey(environment, actorId));
      } else {
        window.localStorage.setItem(activeRunStorageKey(environment, actorId), created.run_id);
        void poll(created.run_id, generation);
      }
    } catch (caught) {
      if (generation === pollGeneration.current) {
        if (!retryableFailure(caught)) {
          window.localStorage.removeItem(pendingRunStorageKey(environment, actorId));
          startKey.current = null;
        } else {
          setRestoring(true);
          setRecoveryNonce((current) => current + 1);
        }
        const detail = (caught as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
        setError(detail || 'Could not start company batch processing.');
      }
    } finally {
      if (generation === pollGeneration.current) setStarting(false);
    }
  };

  const toggleCompany = (company: BatchCompanyCandidate) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(company.company_id) ? next.delete(company.company_id) : next.add(company.company_id);
      return next;
    });
    setSelectedCompanies((current) => {
      const next = new Map(current);
      next.has(company.company_id)
        ? next.delete(company.company_id)
        : next.set(company.company_id, company);
      return next;
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 2fr) minmax(300px, 1fr)',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Box>
          <Typography variant="h6" component="h2">Batch processing</Typography>
          <Typography variant="body2" color="text.secondary">
            Select companies, analyses, and a {daysBack}-day document lookback.
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {groupFilterError && <Alert severity="warning">{groupFilterError}</Alert>}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="batch-server-label">Server</InputLabel>
            <Select
              labelId="batch-server-label"
              label="Server"
              value={serverId}
              onChange={(event) => setServerId(event.target.value)}
            >
              <MenuItem value="">All companies</MenuItem>
              {servers.map((server) => (
                <MenuItem key={server.id} value={server.id}>{server.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} disabled={!serverId || channelsLoading}>
            <InputLabel id="batch-focus-area-label">Focus area</InputLabel>
            <Select
              labelId="batch-focus-area-label"
              label="Focus area"
              value={channelId}
              onChange={(event) => setChannelId(event.target.value)}
            >
              <MenuItem value="">All focus areas</MenuItem>
              {channels.map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.name} ({channel.company_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search companies"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="batch-corporate-status-label">Corporate status</InputLabel>
            <Select
              labelId="batch-corporate-status-label"
              label="Corporate status"
              value={corporateStatus}
              onChange={(event) => setCorporateStatus(event.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {CORPORATE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 190 }}>
            <InputLabel id="batch-coverage-priority-label">Coverage priority</InputLabel>
            <Select
              labelId="batch-coverage-priority-label"
              label="Coverage priority"
              value={coveragePriority}
              onChange={(event) => setCoveragePriority(event.target.value)}
            >
              <MenuItem value="">All priorities</MenuItem>
              {COVERAGE_PRIORITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
              <MenuItem value="unset">Unset</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAllMatching}
              onChange={(event) => {
                setSelectAllMatching(event.target.checked);
                if (event.target.checked) {
                  setSelected(new Set());
                  setSelectedCompanies(new Map());
                }
              }}
            />
          }
          label="Process all matching companies"
        />
        <List dense sx={{ maxHeight: 360, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
          {loading ? (
            <ListItem><CircularProgress size={22} /></ListItem>
          ) : companies.map((company) => (
            <ListItem key={company.company_id} divider>
              <Checkbox
                edge="start"
                inputProps={{ 'aria-label': `Select ${company.company_name}` }}
                checked={selected.has(company.company_id)}
                disabled={selectAllMatching}
                onChange={() => toggleCompany(company)}
              />
              <ListItemText
                primary={company.company_name}
                secondary={[
                  company.ticker,
                  company.corporate_status,
                  company.coverage_priority,
                  `${company.press_release_count} PR${company.press_release_count === 1 ? '' : 's'}`,
                  `${company.presentation_count} presentation${company.presentation_count === 1 ? '' : 's'}`,
                  `${company.document_count} document${company.document_count === 1 ? '' : 's'}`,
                ].filter(Boolean).join(' · ')}
              />
            </ListItem>
          ))}
        </List>
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            Selected companies: {selectAllMatching
              ? 'All companies matching the current filters'
              : selectedCompanyNames.length > 0
              ? selectedCompanyNames.join(', ')
              : 'None'}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {selectedDocumentCount} total document{selectedDocumentCount === 1 ? '' : 's'} to process
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedDocumentCount} document{selectedDocumentCount === 1 ? '' : 's'} selected for processing
          </Typography>
        </Stack>
        {hasMore && (
          <Button
            onClick={() => void load(companies.length, true)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more companies'}
          </Button>
        )}
        <FormGroup row>
          {(['milestones', 'pipeline_intelligence', 'corporate_transactions'] as ExtractorName[]).map((name) => (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  checked={analyses[name]}
                  onChange={(event) => setAnalyses((current) => ({ ...current, [name]: event.target.checked }))}
                />
              }
              label={analysisLabel(name)}
            />
          ))}
        </FormGroup>
        <FormControlLabel
          control={<Checkbox checked={overrideProcessed} onChange={(event) => setOverrideProcessed(event.target.checked)} />}
          label="Override processed documents"
        />
        {analyses.corporate_transactions && (
          <FormControlLabel
            control={<Checkbox checked={confirmCorporateWrites} onChange={(event) => setConfirmCorporateWrites(event.target.checked)} />}
            label="Confirm canonical Corporate Transaction writes"
          />
        )}
        <Button
          variant="contained"
          onClick={() => void start()}
          disabled={
            starting || restoring || Boolean(run && !terminalRun(run)) || selectedDocumentCount === 0
          }
        >
          {restoring
            ? 'Restoring batch…'
            : starting
            ? 'Starting…'
            : run && !terminalRun(run)
            ? 'Batch processing…'
            : 'Start batch processing'}
        </Button>
        {run && (
          <Alert severity={run.status === 'failed' ? 'error' : run.status === 'partial' ? 'warning' : 'info'}>
            <Stack spacing={0.5}>
              {run.current_company_name && !terminalRun(run) && (
                <Typography variant="body2" fontWeight={700}>
                  Processing {run.current_company_name}
                </Typography>
              )}
              <Typography variant="body2">
                Selected companies: {(run.company_summaries && run.company_summaries.length > 0)
                  ? run.company_summaries.map((company) =>
                      `${company.company_name} (${company.documents_total} document${company.documents_total === 1 ? '' : 's'})`
                    ).join(', ')
                  : (run.company_names && run.company_names.length > 0)
                  ? run.company_names.join(', ')
                  : run.current_company_name || `${run.companies_total} selected`}
              </Typography>
              {run.lookback_days != null && (
                <Typography variant="body2">
                  Lookback reviewed: {run.lookback_days} day{run.lookback_days === 1 ? '' : 's'}
                </Typography>
              )}
              <Typography variant="body2">
                {run.documents_total} total document{run.documents_total === 1 ? '' : 's'} to process
              </Typography>
              <Typography variant="body2">
                {run.companies_completed} of {run.companies_total} companies completed ·{' '}
                {run.documents_completed} of {run.documents_total} documents checkpointed
              </Typography>
              <Typography variant="caption">Run {run.status}</Typography>
            </Stack>
          </Alert>
        )}
        </Stack>
        <Paper
          component="aside"
          variant="outlined"
          sx={{
            p: 2,
            maxHeight: { lg: 680 },
            overflowY: 'auto',
            position: { lg: 'sticky' },
            top: { lg: 16 },
          }}
        >
          <Typography variant="h6" component="h3">Company activity</Typography>
          <Typography variant="caption" color="text.secondary">
            Most recently completed first
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {companyActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Company statistics will appear here as each company completes.
              </Typography>
            ) : companyActivity.map((company) => (
              <Box
                key={`${company.company_id}:${company.completed_at}`}
                data-testid="company-activity-entry"
                sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {company.company_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={
                      company.status === 'failed'
                        ? 'error.main'
                        : company.status === 'partial'
                        ? 'warning.main'
                        : 'success.main'
                    }
                  >
                    {company.status}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  {company.documents_completed} of {company.documents_total} documents ·{' '}
                  {new Date(company.completed_at).toLocaleString()}
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                  {company.analyses.map((analysis) => {
                    const errorMessages = analysisErrorMessages(analysis.error);
                    return (
                      <Box key={analysis.extractor}>
                        <Typography variant="body2">
                          {analysisLabel(analysis.extractor)}: {analysis.item_count} found ·{' '}
                          {analysis.saved_count} saved
                        </Typography>
                        {errorMessages.map((message) => (
                          <Typography
                            key={message}
                            variant="caption"
                            color="error.main"
                            display="block"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            Not saved: {message}
                          </Typography>
                        ))}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Paper>
  );
};

export default BatchProcessingPanel;
